import withNamespace from '../util/withNamespace';
import { alias } from '../util/alias';
import { hexToAlpha } from '../util/hexToAlpha';
import { AVATAR_BADGE_BADGE_SIZES, AVATAR_BADGE_ICON_SIZES } from '../constants/AVATAR_BADGE';
import { AVATAR_SIZES, AVATAR_FONT_SIZES } from '../constants/AVATAR';
import { BUTTON_TYPES } from '../constants/BUTTON';
import { DIVIDER_APPEARANCES } from '../constants/DIVIDER';
import { FONT_TYPES } from '../constants/FONT_TYPES';
import { ICON_SIZES, ICON_STATUS_TYPES } from '../constants/ICON';
import { INPUT_SIZES } from '../constants/INPUT';
import { LABEL_SIZES } from '../constants/LABEL';
import { MODAL_SIZES } from '../components/ModalDialog/MODAL_SIZES';
import { PROGRESS_METER_SIZES } from '../constants/PROGRESS_METER';
import { PROGRESS_STEPPER_APPEARANCES } from '../components/ProgressStepper/PROGRESS_STEPPER_APPEARANCES';
import { PROPERTY_CARD_APPEARANCES, PROPERTY_CARD_BADGE_APPEARANCES } from '../constants/PROPERTY_CARD';
import { T_SHIRT_SIZES } from '../constants/T_SHIRT_SIZES';
import { STATUS_TYPES } from '../constants/STATUS';
import { SWITCH_SIZES } from '../constants/SWITCH';
import { TABLE_SIZES } from '../constants/TABLE';
import { TAG_APPEARANCES, TAG_SIZES } from '../constants/TAG';
import { FONT_FAMILIES } from '../constants/FONT_FAMILIES';

var getConstellationTheme = function getConstellationTheme() {
  var _fontSizes, _fontWeights, _sizes, _sizes2, _lineHeights, _heights;

  var theme = {
    accessibility: {
      targetSize: 44
    },
    animations: {
      defaultDuration: 0.3,
      defaultTimingFunction: 'ease-out'
    },
    breakpoints: {
      xs: 0,
      sm: 321,
      md: 481,
      lg: 769,
      xl: 1024,
      xxl: 1281
    },
    colors: {
      white: '#FFF',
      gray100: '#F9F9FB',
      gray200: '#F1F1F4',
      gray300: '#D1D1D5',
      gray400: '#A7A6AB',
      gray500: '#54545A',
      gray600: '#2A2A33',
      black: '#0A0A14',
      trueBlack: '#000',
      coolGray100: '#F6F6FA',
      coolGray200: '#EFF3F9',
      coolGray300: '#DEE3E6',
      coolGray400: '#A6B3C4',
      coolGray500: '#596B82',
      coolGray600: '#313F51',
      blue100: '#F2FAFF',
      blue200: '#E0F2FF',
      blue300: '#A6E5FF',
      blue375: '#579DFF',
      blue400: '#006AFF',
      blue500: '#0D4599',
      blue600: '#001751',
      yellow100: '#FFF8E1',
      yellow200: '#FFDC69',
      yellow300: '#FFD237',
      yellow400: '#F2A619',
      yellow500: '#C88205',
      yellow600: '#8A5700',
      orange100: '#F8CFBA',
      orange200: '#F3AE8B',
      orange300: '#EE8E5D',
      orange400: '#E96E2F',
      orange500: '#D05416',
      orange600: '#933100',
      green100: '#D8F4DE',
      green200: '#63E37D',
      green300: '#3DCA5A',
      green400: '#17B036',
      green500: '#128A29',
      green600: '#055E16',
      aqua100: '#D5F3F9',
      aqua200: '#50E3FF',
      aqua300: '#2BC1E1',
      aqua400: '#059EC2',
      aqua500: '#0484A7',
      aqua600: '#03698C',
      purple100: '#F0E3FF',
      purple200: '#CE8DFF',
      purple300: '#B375FF',
      purple400: '#985DFF',
      purple500: '#7A48D6',
      purple600: '#5B33AD',
      red100: '#FFE3E2',
      red200: '#FFADB0',
      red300: '#FF7370',
      red400: '#FF5A50',
      red500: '#E82B27',
      red600: '#A3000B',
      orangeRed100: '#FFBFAD',
      orangeRed200: '#FF9679',
      orangeRed300: '#FF6B45',
      orangeRed400: '#DB3A00',
      orangeRed500: '#BE1C00',
      orangeRed600: '#AF1500',
      facebook: '#1877F2',
      twitter: '#55ACEE',
      linkedIn: '#0077B5',
      none: 'none'
    },
    defaultUnit: 'px',
    fonts: {
      sansSerif: FONT_FAMILIES.SANS_SERIF.join(','),
      serif: FONT_FAMILIES.SERIF.join(',')
    },
    fontSizes: (_fontSizes = {}, _fontSizes[FONT_TYPES.h0] = 96, _fontSizes[FONT_TYPES.h1] = 60, _fontSizes[FONT_TYPES.h2] = 48, _fontSizes[FONT_TYPES.h3] = 36, _fontSizes[FONT_TYPES.h4] = 24, _fontSizes[FONT_TYPES.h5] = 20, _fontSizes[FONT_TYPES.h6] = 16, _fontSizes[FONT_TYPES.bodyHeading] = 16, _fontSizes[FONT_TYPES.body] = 16, _fontSizes[FONT_TYPES.bodySmallHeading] = 14, _fontSizes[FONT_TYPES.bodySmall] = 14, _fontSizes[FONT_TYPES.finePrintHeading] = 12, _fontSizes[FONT_TYPES.finePrint] = 12, _fontSizes[FONT_TYPES.legal] = 10, _fontSizes),
    fontWeights: (_fontWeights = {}, _fontWeights[FONT_TYPES.h0] = 700, _fontWeights[FONT_TYPES.h1] = 700, _fontWeights[FONT_TYPES.h2] = 700, _fontWeights[FONT_TYPES.h3] = 700, _fontWeights[FONT_TYPES.h4] = 700, _fontWeights[FONT_TYPES.h5] = 700, _fontWeights[FONT_TYPES.h6] = 700, _fontWeights[FONT_TYPES.bodyHeading] = 700, _fontWeights[FONT_TYPES.body] = 400, _fontWeights[FONT_TYPES.bodySmallHeading] = 700, _fontWeights[FONT_TYPES.bodySmall] = 400, _fontWeights[FONT_TYPES.finePrintHeading] = 700, _fontWeights[FONT_TYPES.finePrint] = 400, _fontWeights[FONT_TYPES.legal] = 400, _fontWeights),
    gridBase: 8,
    icons: {
      sizes: (_sizes = {}, _sizes[ICON_SIZES.xs] = 16, _sizes[ICON_SIZES.sm] = 24, _sizes[ICON_SIZES.md] = 32, _sizes[ICON_SIZES.lg] = 44, _sizes)
    },
    detailedIcons: {
      sizes: (_sizes2 = {}, _sizes2[ICON_SIZES.sm] = 44, _sizes2)
    },
    lineHeights: (_lineHeights = {}, _lineHeights[FONT_TYPES.h0] = 112, _lineHeights[FONT_TYPES.h1] = 72, _lineHeights[FONT_TYPES.h2] = 64, _lineHeights[FONT_TYPES.h3] = 40, _lineHeights[FONT_TYPES.h4] = 32, _lineHeights[FONT_TYPES.h5] = 24, _lineHeights[FONT_TYPES.h6] = 24, _lineHeights[FONT_TYPES.bodyHeading] = 24, _lineHeights[FONT_TYPES.body] = 24, _lineHeights[FONT_TYPES.bodySmallHeading] = 24, _lineHeights[FONT_TYPES.bodySmall] = 24, _lineHeights[FONT_TYPES.finePrintHeading] = 16, _lineHeights[FONT_TYPES.finePrint] = 16, _lineHeights[FONT_TYPES.legal] = 16, _lineHeights),
    opacities: {
      opacity100: 0.2,
      opacity200: 0.4,
      opacity300: 0.6,
      opacity400: 0.8
    },
    radii: {
      sm: 2,
      md: 4,
      lg: 8,
      ellipse: '50%',
      pill: '999px'
    },
    shadows: {
      shadow100: '0 2px 4px 0 rgba(0, 0, 0, 0.3)',
      shadow200: '0 4px 8px 0 rgba(0, 0, 0, 0.3)',
      shadow300: '0 8px 12px 0 rgba(0, 0, 0, 0.3)',
      shadow400: '0 15px 30px 0 rgba(0, 0, 0, 0.3)'
    },
    spacing: {
      xs: 8,
      sm: 16,
      md: 24,
      lg: 32,
      xl: 48
    },
    sizes: {
      heights: (_heights = {}, _heights[T_SHIRT_SIZES.xs] = 28, _heights[T_SHIRT_SIZES.sm] = 36, _heights[T_SHIRT_SIZES.md] = 44, _heights[T_SHIRT_SIZES.lg] = 56, _heights)
    },
    zIndices: {
      modal: 100002,
      popover: 100011,
      dialog: 100013,
      toast: 200000
    }
  }; // Tokens and alias that depend on the core theme values

  alias(theme, function (t) {
    return {
      colors: {
        // Brand colors
        brand: t.colors.blue400,
        brandSecondary: t.colors.yellow400,
        backgroundBrand: t.colors.blue400,
        // Text colors
        textWhite: t.colors.white,
        textLight: t.colors.gray400,
        textMedium: t.colors.coolGray500,
        textDark: t.colors.gray600,
        // Link colors
        link: t.colors.blue500,
        linkVisited: t.colors.purple600,
        linkHover: t.colors.blue600,
        linkActive: t.colors.blue600,
        // Background colors (light)
        backgroundAqua: t.colors.aqua100,
        backgroundBlue: t.colors.blue100,
        backgroundGreen: t.colors.green100,
        backgroundOrange: t.colors.orange100,
        backgroundOrangeRed: t.colors.orangeRed100,
        backgroundPurple: t.colors.purple100,
        backgroundRed: t.colors.red100,
        backgroundYellow: t.colors.yellow100,
        // Background colors (dark)
        backgroundDarkAqua: t.colors.aqua600,
        backgroundDarkBlue: t.colors.blue600,
        backgroundDarkGreen: t.colors.green600,
        backgroundDarkOrange: t.colors.orange600,
        backgroundDarkOrangeRed: t.colors.orangeRed600,
        backgroundDarkPurple: t.colors.purple600,
        backgroundDarkRed: t.colors.red600,
        backgroundDarkYellow: t.colors.yellow600,
        // Background colors (grayscale)
        backgroundWhite: t.colors.white,
        backgroundLight: t.colors.gray100,
        backgroundDark: t.colors.gray600,
        // Border colors
        borderAqua: t.colors.aqua500,
        borderBlue: t.colors.blue500,
        borderGreen: t.colors.green500,
        borderOrange: t.colors.orange500,
        borderOrangeRed: t.colors.red500,
        borderPurple: t.colors.purple500,
        borderRed: t.colors.red500,
        borderYellow: t.colors.yellow500,
        // Border colors (grayscale)
        borderLight: t.colors.gray300,
        borderDark: t.colors.gray400,
        // Home type colors
        offMarket: t.colors.gray400,
        comingSoon: t.colors.blue400,
        recentlySold: t.colors.yellow300,
        forRent: t.colors.purple400,
        forSale: t.colors.red400,
        // backward compatable primary aliases
        primary100: t.colors.blue100,
        primary200: t.colors.blue200,
        primary300: t.colors.blue300,
        primary400: t.colors.blue400,
        primary500: t.colors.blue500,
        primary600: t.colors.blue600,
        // backwards compatible secondary aliases
        secondary100: t.colors.yellow100,
        secondary200: t.colors.yellow200,
        secondary300: t.colors.yellow300,
        secondary400: t.colors.yellow400,
        secondary500: t.colors.yellow500,
        secondary600: t.colors.yellow600
      },
      grid: {
        columns: 12,
        // This is calculated based on an ideal 1280 screen width:
        // (12 columns * 88) + (11 gaps * 16) + (24 left margin) + (24 right margin)
        columnWidth: 88,
        gap: t.spacing.sm
      },
      opacities: {
        disabled: t.opacities.opacity200
      },
      shadows: {
        focus: "0 0 0px 1px " + t.colors.white + ",0 0 2px 3px " + t.colors.blue300 + ",0 0 2px 4px " + t.colors.blue400
      }
    };
  }); // Status aliases

  alias(theme, function (t) {
    var _status;

    return {
      status: (_status = {
        fontType: FONT_TYPES.body,
        iconSize: t.icons.sizes[ICON_SIZES.sm]
      }, _status[STATUS_TYPES.error] = {
        background: t.colors.backgroundRed,
        border: t.colors.borderRed,
        foreground: t.colors.red500,
        fontColor: t.colors.red600,
        darkBackground: t.colors.red600
      }, _status[STATUS_TYPES.info] = {
        background: t.colors.backgroundBlue,
        border: t.colors.borderBlue,
        foreground: t.colors.blue600,
        fontColor: t.colors.blue600,
        darkBackground: t.colors.blue600
      }, _status[STATUS_TYPES.success] = {
        background: t.colors.backgroundGreen,
        border: t.colors.borderGreen,
        foreground: t.colors.green500,
        fontColor: t.colors.green600,
        darkBackground: t.colors.green600
      }, _status[STATUS_TYPES.warning] = {
        background: t.colors.backgroundYellow,
        border: t.colors.borderYellow,
        foreground: t.colors.orange400,
        fontColor: t.colors.yellow600,
        darkBackground: t.colors.orange500
      }, _status.transient = {
        foreground: t.colors.blue400
      }, _status)
    };
  }); // Component tokens

  alias(theme, function (t) {
    var _sizes3, _fontTypes, _badgeSizes, _iconSizes, _Button, _DetailedIcon, _Divider, _fontTypes2, _sizes4, _sizes5, _sizes6, _status2, _backgroundColor, _opacity, _hover, _boxShadow, _gridTemplate, _fontType, _size, _gridTemplateAreas, _maxWidth, _minWidth, _fontType2, _width, _sizes7, _fontType3, _headerFontType, _horizontalPadding, _verticalPadding, _Object$assign, _fontTypes3;

    return {
      AccordionHeader: {
        backgroundColor: t.colors.white,
        backgroundColorHover: t.colors.coolGray100,
        borderRadius: t.radii.md,
        padding: t.spacing.sm
      },
      AccordionItem: {
        borderColor: t.colors.gray300,
        borderRadius: t.radii.md,
        borderSize: 1,
        spacing: t.spacing.xs
      },
      AccordionPanel: {
        backgroundColor: t.colors.white,
        borderColor: t.colors.gray300,
        borderRadius: t.radii.md,
        borderSize: 1,
        padding: t.spacing.sm
      },
      Avatar: {
        sizes: (_sizes3 = {}, _sizes3[AVATAR_SIZES.xs] = 24, _sizes3[AVATAR_SIZES.sm] = 32, _sizes3[AVATAR_SIZES.md] = 48, _sizes3[AVATAR_SIZES.lg] = 64, _sizes3[AVATAR_SIZES.xl] = 96, _sizes3[AVATAR_SIZES.xxl] = 160, _sizes3),
        fontTypes: (_fontTypes = {}, _fontTypes[AVATAR_FONT_SIZES.xs] = FONT_TYPES.bodySmallHeading, _fontTypes[AVATAR_FONT_SIZES.sm] = FONT_TYPES.h6, _fontTypes[AVATAR_FONT_SIZES.md] = FONT_TYPES.h5, _fontTypes[AVATAR_FONT_SIZES.lg] = FONT_TYPES.h4, _fontTypes[AVATAR_FONT_SIZES.xl] = FONT_TYPES.h3, _fontTypes[AVATAR_FONT_SIZES.xxl] = FONT_TYPES.h1, _fontTypes),
        iconColor: t.colors.gray400,
        backgroundColor: t.colors.gray200
      },
      AvatarBadge: {
        badgeSizes: (_badgeSizes = {}, _badgeSizes[AVATAR_BADGE_BADGE_SIZES.xxl] = 48, _badgeSizes[AVATAR_BADGE_BADGE_SIZES.xl] = 36, _badgeSizes[AVATAR_BADGE_BADGE_SIZES.lg] = 28, _badgeSizes),
        iconSizes: (_iconSizes = {}, _iconSizes[AVATAR_BADGE_ICON_SIZES.xxl] = 32, _iconSizes[AVATAR_BADGE_ICON_SIZES.xl] = 24, _iconSizes[AVATAR_BADGE_ICON_SIZES.lg] = 16, _iconSizes),
        backgroundColor: t.colors.brand,
        foregroundColor: t.colors.white,
        borderColor: t.colors.white
      },
      Button: (_Button = {
        fontType: FONT_TYPES.bodyHeading,
        fontTypeSmall: FONT_TYPES.bodySmallHeading,
        borderRadius: t.radii.md,
        borderWidth: 1,
        horizontalPadding: t.spacing.sm
      }, _Button[BUTTON_TYPES.primary] = {
        fontColor: t.colors.white,
        fontColorHover: t.colors.white,
        fontColorActive: t.colors.white,
        fontColorPressed: t.colors.white,
        borderColor: t.colors.brand,
        borderColorHover: t.colors.blue500,
        borderColorActive: t.colors.blue600,
        borderColorPressed: t.colors.blue600,
        backgroundColor: t.colors.brand,
        backgroundColorHover: t.colors.blue500,
        backgroundColorActive: t.colors.blue600,
        backgroundColorPressed: t.colors.blue600
      }, _Button[BUTTON_TYPES.secondary] = {
        fontColor: t.colors.brand,
        fontColorHover: t.colors.blue500,
        fontColorActive: t.colors.blue600,
        fontColorPressed: t.colors.white,
        borderColor: t.colors.brand,
        borderColorHover: t.colors.blue500,
        borderColorActive: t.colors.blue600,
        borderColorPressed: t.colors.blue600,
        backgroundColor: t.colors.white,
        backgroundColorHover: t.colors.blue100,
        backgroundColorActive: t.colors.blue200,
        backgroundColorPressed: t.colors.blue600
      }, _Button[BUTTON_TYPES.tertiary] = {
        fontColor: t.colors.gray600,
        fontColorHover: t.colors.gray600,
        fontColorActive: t.colors.gray600,
        fontColorPressed: t.colors.gray600,
        borderColor: t.colors.gray400,
        borderColorHover: t.colors.gray400,
        borderColorActive: t.colors.gray500,
        borderColorPressed: t.colors.brand,
        backgroundColor: t.colors.white,
        backgroundColorHover: t.colors.gray200,
        backgroundColorActive: t.colors.gray300,
        backgroundColorPressed: t.colors.blue100
      }, _Button[BUTTON_TYPES.caution] = {
        fontColor: t.colors.white,
        fontColorHover: t.colors.red600,
        fontColorActive: t.colors.white,
        fontColorPressed: t.colors.white,
        borderColor: t.colors.red500,
        borderColorHover: t.colors.red600,
        borderColorActive: t.colors.red600,
        borderColorPressed: t.colors.red600,
        backgroundColor: t.colors.red500,
        backgroundColorHover: t.colors.white,
        backgroundColorActive: t.colors.red600,
        backgroundColorPressed: t.colors.red600
      }, _Button[BUTTON_TYPES.primary + "Inverse"] = {
        fontColor: t.colors.brand,
        fontColorHover: t.colors.blue500,
        fontColorActive: t.colors.blue600,
        borderColor: t.colors.white,
        borderColorHover: t.colors.white,
        borderColorActive: t.colors.white,
        backgroundColor: t.colors.white,
        backgroundColorHover: t.colors.blue100,
        backgroundColorActive: t.colors.blue200
      }, _Button[BUTTON_TYPES.secondary + "Inverse"] = {
        fontColor: t.colors.white,
        fontColorHover: t.colors.gray600,
        fontColorActive: t.colors.gray600,
        borderColor: t.colors.white,
        borderColorHover: t.colors.white,
        borderColorActive: t.colors.white,
        backgroundColor: 'transparent',
        backgroundColorHover: t.colors.gray200,
        backgroundColorActive: t.colors.gray300
      }, _Button),
      Card: {
        borderColor: t.colors.borderLight,
        borderColorPrimary: t.colors.brand,
        backgroundColor: t.colors.white,
        separatorColor: t.colors.borderLight
      },
      CardButton: {
        fontType: FONT_TYPES.h6,
        fontColor: t.colors.brand
      },
      CardHeader: {
        fontType: FONT_TYPES.h5
      },
      CloseButton: {
        buttonSize: t.lineHeights[FONT_TYPES.body],
        iconColor: t.colors.black,
        backgroundColor: t.colors.gray200,
        backgroundColorInverse: 'rgba(255, 255, 255, 0.3)'
      },
      ComboboxInput: {
        tagMargin: 0.5 * t.gridBase,
        minInputWidth: '25%'
      },
      DatePicker: {
        caretSize: 28,
        caretIconSize: 10
      },
      DetailedIcon: (_DetailedIcon = {
        noFill: t.colors.none,
        white: t.colors.white
      }, _DetailedIcon[ICON_STATUS_TYPES.info] = {
        foregroundColor: t.colors.blue500,
        secondaryColor: t.colors.blue300,
        accentColor: t.colors.yellow400
      }, _DetailedIcon[ICON_STATUS_TYPES.success] = {
        foregroundColor: t.colors.green600,
        secondaryColor: t.colors.green200,
        accentColor: t.colors.yellow400
      }, _DetailedIcon),
      Dialog: {
        minWidth: 200,
        maxWidth: 360,
        headingFontType: FONT_TYPES.h5,
        backgroundColor: t.colors.white,
        boxShadow: t.shadows.shadow300
      },
      Divider: (_Divider = {
        lengths: {
          auto: 'auto',
          fixed: 144,
          fluid: '100%'
        }
      }, _Divider[DIVIDER_APPEARANCES.regular] = {
        backgroundColor: t.colors.gray300,
        thickness: 1
      }, _Divider[DIVIDER_APPEARANCES.emphasized] = {
        backgroundColor: t.colors.yellow300,
        thickness: 2
      }, _Divider),
      Dropdown: {
        horizontalSpacing: t.spacing.sm,
        verticalSpacing: 1.5 * t.gridBase
      },
      DropdownHeading: {
        fontType: FONT_TYPES.bodySmallHeading,
        fontColor: t.colors.textMedium,
        iconSize: ICON_SIZES.sm,
        backgroundColor: t.colors.coolGray100,
        horizontalSpacing: t.spacing.sm,
        verticalSpacing: 1.5 * t.gridBase
      },
      DropdownIcon: {
        iconColor: t.colors.brand
      },
      FormHelp: {
        fontType: FONT_TYPES.finePrint,
        fontColor: t.colors.textMedium
      },
      Gleam: {
        height: 20,
        padding: 6,
        fontColor: t.colors.textWhite,
        fontType: FONT_TYPES.bodySmallHeading,
        backgroundColor: t.colors.orangeRed400
      },
      GridContent: {
        backgroundColor: t.colors.backgroundWhite,
        borderRadius: t.radii.md
      },
      Input: {
        fontType: FONT_TYPES.body,
        borderWidth: 1,
        horizontalSpacing: t.spacing.sm,
        fontColor: t.colors.textDark,
        fontColorPlaceholder: t.colors.textMedium,
        backgroundColor: t.colors.coolGray100,
        backgroundColorAutofill: t.colors.blue200,
        backgroundColorInteractive: t.colors.white,
        borderColor: t.colors.borderLight,
        // Make this match error help text color
        borderColorError: t.status.error.fontColor,
        borderColorInteractive: t.colors.brand,
        adornmentColor: t.colors.gray500
      },
      Label: {
        fontTypes: (_fontTypes2 = {}, _fontTypes2[LABEL_SIZES.md] = FONT_TYPES.bodySmallHeading, _fontTypes2[LABEL_SIZES.lg] = FONT_TYPES.bodyHeading, _fontTypes2),
        asteriskColor: t.status.error.fontColor,
        optionalColor: t.colors.textMedium
      },
      LabeledControl: {
        fontType: FONT_TYPES.body
      },
      LabeledInput: {
        fontTypeInput: FONT_TYPES.body,
        fontTypeLabel: FONT_TYPES.finePrintHeading,
        scale: t.fontSizes[FONT_TYPES.finePrintHeading] / t.fontSizes[FONT_TYPES.body],
        transition: '0.15s all',
        verticalSpacing: t.spacing.xs,
        horizontalSpacing: t.spacing.sm,
        // paddingBase is calculated based on
        // sizeMixin with size: "lg"
        paddingBase: 15
      },
      List: {
        fontType: FONT_TYPES.body,
        borderColor: t.colors.borderLight,
        borderWidth: 1
      },
      Listbox: {
        sizes: (_sizes4 = {}, _sizes4[INPUT_SIZES.md] = 220, _sizes4)
      },
      ListboxGroupLabel: {
        fontType: FONT_TYPES.bodySmallHeading,
        fontColor: t.colors.textMedium,
        backgroundColor: t.colors.coolGray100
      },
      ListboxOption: {
        backgroundColorActive: t.colors.backgroundBlue,
        fontType: FONT_TYPES.body,
        iconSize: ICON_SIZES.sm,
        iconColor: t.colors.textMedium,
        mediaSize: t.icons.sizes.sm,
        metaColor: t.colors.textMedium,
        horizontalSpacing: t.spacing.sm,
        verticalSpacing: 1.5 * t.gridBase
      },
      MenuContent: {
        fontType: FONT_TYPES.finePrint,
        horizontalSpacing: t.spacing.sm,
        verticalSpacing: 1.5 * t.gridBase
      },
      MenuItem: {
        backgroundColor: t.colors.backgroundWhite,
        backgroundColorHover: t.colors.backgroundBlue,
        fontColor: t.colors.textDark,
        fontType: FONT_TYPES.body,
        iconSize: ICON_SIZES.sm,
        iconColor: t.colors.textMedium,
        horizontalSpacing: t.spacing.sm,
        verticalSpacing: 1.5 * t.gridBase
      },
      Modal: {
        viewportPaddingVertical: t.spacing.xl,
        viewportPaddingHorizontal: t.spacing.sm
      },
      ModalDialog: {
        padding: t.spacing.sm,
        separatorColor: t.colors.borderLight,
        sizes: (_sizes5 = {}, _sizes5[MODAL_SIZES.XS] = 360, _sizes5[MODAL_SIZES.SM] = 540, _sizes5[MODAL_SIZES.MD] = 720, _sizes5[MODAL_SIZES.LG] = 900, _sizes5[MODAL_SIZES.XL] = 1080, _sizes5[MODAL_SIZES.FULL_SCREEN] = 100, _sizes5)
      },
      Page: {
        backgroundColor: t.colors.coolGray100,
        maxWidth: t.grid.columns * t.grid.columnWidth + (t.grid.columns - 1) * t.grid.gap,
        horizontalPadding: t.spacing.md,
        horizontalPaddingMobile: t.spacing.sm,
        verticalMargin: t.spacing.lg
      },
      Popover: {
        maxHeight: 320,
        headingFontType: FONT_TYPES.h6,
        padding: t.spacing.sm,
        // This is the size of the square the arrow will reside in,
        // including space for box shadow spread.
        arrowOuterSize: 40,
        // This is the size of the actual arrow content,
        // and comes from the pythagorean theorem (a^2 + b^2 = c^2),
        // where c is the desired width of 24 when the arrow is rotated 45 degrees
        arrowInnerSize: 17,
        // Alignment for the inner box to create the visual triangle
        arrowAlignStart: -9,
        arrowAlignCenter: 12,
        arrowAlignEnd: 31,
        separatorColor: t.colors.borderLight
      },
      ProgressMeter: {
        sizes: (_sizes6 = {}, _sizes6[PROGRESS_METER_SIZES.sm] = 8, _sizes6[PROGRESS_METER_SIZES.md] = 16, _sizes6),
        backgroundColor: t.colors.coolGray400,
        borderRadius: t.radii.md,
        indicatorColorInProgress: t.colors.blue500,
        indicatorColorCompleted: t.colors.green300,
        transition: '0.2s ease-in-out'
      },
      ProgressStepper: {
        currentStepSize: 36,
        nonInteractiveStepSize: 4,
        interactiveStepSize: 20,
        nonInteractiveLabelColor: t.colors.textMedium,
        interactiveLabelColor: t.colors.blue500,
        stepBorderWidth: 6,
        stepPaddingX: 2,
        tagMarginTop: 0.5 * t.gridBase,
        labelFontType: FONT_TYPES.bodySmall,
        labelOffset: 12,
        backgroundColor: t.colors.coolGray400,
        indicatorColor: t.colors.blue500,
        stepBackgroundColor: t.colors.white,
        activeStepBackgroundColor: t.colors.brand,
        activeStepBorderColor: t.colors.white,
        activeStepLabelColor: t.colors.textDark,
        activeStepBoxShadow: "inset 0 0 0 2px " + t.colors.brand,
        boxShadowRing: "0 0 0 8px " + hexToAlpha(t.colors.blue500, 0.2),
        status: (_status2 = {}, _status2[PROGRESS_STEPPER_APPEARANCES.COMPLETE] = {
          foreground: t.colors.blue500,
          boxShadow: "inset 0 0 0 2px " + t.colors.blue500
        }, _status2[PROGRESS_STEPPER_APPEARANCES.INCOMPLETE] = {
          foreground: t.colors.blue500,
          boxShadow: "inset 0 0 0 2px " + t.colors.blue500
        }, _status2[PROGRESS_STEPPER_APPEARANCES.WARNING] = {
          foreground: t.status.warning.foreground,
          boxShadow: "inset 0 0 0 2px " + t.colors.blue500,
          iconSize: t.icons.sizes.sm,
          filler: {
            width: 4,
            height: 12
          }
        }, _status2[PROGRESS_STEPPER_APPEARANCES.ERROR] = {
          foreground: t.status.error.foreground,
          boxShadow: "inset 0 0 0 2px " + t.status.error.foreground
        }, _status2[PROGRESS_STEPPER_APPEARANCES.NONE] = {
          foreground: 'inherit',
          boxShadow: ''
        }, _status2)
      },
      PropertyCard: {
        badge: {
          backgroundColor: (_backgroundColor = {}, _backgroundColor[PROPERTY_CARD_BADGE_APPEARANCES.DEFAULT] = 'black', _backgroundColor[PROPERTY_CARD_BADGE_APPEARANCES.NOTIFICATION] = 'orangeRed400', _backgroundColor[PROPERTY_CARD_BADGE_APPEARANCES.ZILLOW] = 'blue400', _backgroundColor),
          borderRadius: t.radii.pill,
          fontColor: t.colors.textWhite,
          fontType: FONT_TYPES.finePrintHeading,
          horizontalPadding: t.spacing.xs,
          opacity: (_opacity = {}, _opacity[PROPERTY_CARD_BADGE_APPEARANCES.DEFAULT] = t.opacities.opacity300, _opacity[PROPERTY_CARD_BADGE_APPEARANCES.NOTIFICATION] = 1, _opacity[PROPERTY_CARD_BADGE_APPEARANCES.ZILLOW] = 1, _opacity),
          verticalPadding: 2
        },
        cardBody: {
          borderRadius: t.radii.md,
          boxShadow: (_boxShadow = {}, _boxShadow[PROPERTY_CARD_APPEARANCES.FLAG] = t.shadows.shadow200, _boxShadow.hover = (_hover = {}, _hover[PROPERTY_CARD_APPEARANCES.FLAG] = t.shadows.shadow300, _hover), _boxShadow),
          gridTemplate: (_gridTemplate = {}, _gridTemplate[PROPERTY_CARD_APPEARANCES.LARGE] = '149px 1fr auto', _gridTemplate[PROPERTY_CARD_APPEARANCES.SMALL] = '123px minmax(112px, 1fr)', _gridTemplate[PROPERTY_CARD_APPEARANCES.HORIZONTAL] = '116px 1fr', _gridTemplate[PROPERTY_CARD_APPEARANCES.FLAG] = '120px 1fr', _gridTemplate)
        },
        data: {
          title: {
            fontColor: t.colors.textDark,
            fontType: (_fontType = {}, _fontType[PROPERTY_CARD_APPEARANCES.LARGE] = FONT_TYPES.h5, _fontType[PROPERTY_CARD_APPEARANCES.SMALL] = FONT_TYPES.h6, _fontType[PROPERTY_CARD_APPEARANCES.HORIZONTAL] = FONT_TYPES.h5, _fontType[PROPERTY_CARD_APPEARANCES.FLAG] = FONT_TYPES.h6, _fontType)
          },
          body1: {
            fontColor: t.colors.textDark,
            fontType: FONT_TYPES.bodySmall
          },
          body2: {
            fontColor: t.colors.textDark,
            fontType: FONT_TYPES.bodySmall
          },
          body3: {
            fontColor: t.colors.textDark,
            fontType: FONT_TYPES.bodySmall
          },
          additionalInfo: {
            fontColor: t.colors.textMedium,
            fontType: FONT_TYPES.legal
          }
        },
        fallbackImage: {
          fill: t.colors.coolGray300,
          size: (_size = {}, _size[PROPERTY_CARD_APPEARANCES.LARGE] = 70, _size[PROPERTY_CARD_APPEARANCES.SMALL] = 42, _size[PROPERTY_CARD_APPEARANCES.HORIZONTAL] = 42, _size[PROPERTY_CARD_APPEARANCES.FLAG] = 32, _size)
        },
        gridTemplateAreas: (_gridTemplateAreas = {}, _gridTemplateAreas[PROPERTY_CARD_APPEARANCES.LARGE] = "\"photo\" \"data\" \"flex\"", _gridTemplateAreas[PROPERTY_CARD_APPEARANCES.SMALL] = "\"photo\" \"data\"", _gridTemplateAreas[PROPERTY_CARD_APPEARANCES.HORIZONTAL] = "\"photo data\"", _gridTemplateAreas[PROPERTY_CARD_APPEARANCES.FLAG] = "\"photo data\"", _gridTemplateAreas),
        loading: {
          backgroundColor: t.colors.gray200,
          backgroundImage: "linear-gradient(to right, " + t.colors.gray200 + ", " + t.colors.gray100 + ", " + t.colors.gray200 + ")"
        },
        maxWidth: (_maxWidth = {}, _maxWidth[PROPERTY_CARD_APPEARANCES.LARGE] = 386, _maxWidth[PROPERTY_CARD_APPEARANCES.SMALL] = 244, _maxWidth[PROPERTY_CARD_APPEARANCES.HORIZONTAL] = 393, _maxWidth[PROPERTY_CARD_APPEARANCES.FLAG] = 332, _maxWidth),
        minWidth: (_minWidth = {}, _minWidth[PROPERTY_CARD_APPEARANCES.LARGE] = 286, _minWidth[PROPERTY_CARD_APPEARANCES.SMALL] = 144, _minWidth[PROPERTY_CARD_APPEARANCES.HORIZONTAL] = 193, _minWidth[PROPERTY_CARD_APPEARANCES.FLAG] = 232, _minWidth),
        mls: {
          background: 'linear-gradient(rgba(0, 0, 0, 0) 0%, rgba(0, 0, 0, 0.5) 50%, rgba(0, 0, 0, 0.5) 100%)',
          fontColor: t.colors.textWhite,
          fontType: (_fontType2 = {}, _fontType2[PROPERTY_CARD_APPEARANCES.LARGE] = FONT_TYPES.finePrintHeading, _fontType2[PROPERTY_CARD_APPEARANCES.SMALL] = FONT_TYPES.legal, _fontType2[PROPERTY_CARD_APPEARANCES.HORIZONTAL] = FONT_TYPES.legal, _fontType2[PROPERTY_CARD_APPEARANCES.f] = FONT_TYPES.legal, _fontType2),
          textShadow: '0 1px 1px rgba(0, 0, 0, 0.2)'
        },
        photo: {
          backgroundColor: t.colors.coolGray100
        },
        width: (_width = {}, _width[PROPERTY_CARD_APPEARANCES.FLAG] = 282, _width)
      },
      Radio: {
        backgroundColor: t.colors.white,
        borderColor: t.colors.borderDark,
        borderColorDisabled: t.colors.borderLight,
        borderWidth: 2,
        borderWidthChecked: 5,
        size: 20
      },
      SearchBox: {
        boxShadow: '0 0 18px rgba(0, 0, 0, 0.1)',
        fontType: FONT_TYPES.h6,
        height: 60,
        heightLarge: 70,
        iconSize: 16,
        iconSizeLarge: 22,
        iconColor: t.colors.gray500
      },
      Select: {
        chevronWidth: 40,
        chevronHeight: 28,
        chevronColor: t.colors.gray500
      },
      Slider: {
        thumbBackgroundColor: t.colors.brand,
        thumbBackgroundColorInteractive: t.colors.blue500,
        thumbBorderColor: t.colors.white,
        thumbBorderWidth: 4,
        thumbBoxShadow: t.shadows.shadow100,
        // Unfortunate magic number, but there is no simple way to calculate the offset
        // of the label above the slider thumb.
        thumbLabelOffset: -60,
        thumbSize: 24,
        trackBackgroundColor: t.colors.gray300,
        trackBackgroundColorSelected: t.colors.brand,
        trackHeight: 2
      },
      SliderLabel: {
        caretSize: 8,
        fontType: FONT_TYPES.body,
        fontColor: t.colors.textDark,
        backgroundColor: t.colors.white
      },
      Switch: {
        transition: '0.4s all',
        iconColorOn: t.colors.brand,
        iconColorOff: t.colors.white,
        iconBackgroundColorOn: t.colors.white,
        iconBackgroundColorOff: t.colors.gray300,
        backgroundColor: t.colors.gray200,
        backgroundColorChecked: t.colors.brand,
        sizes: (_sizes7 = {}, _sizes7[SWITCH_SIZES.lg] = {
          height: 30,
          width: 50,
          padding: 5
        }, _sizes7[SWITCH_SIZES.md] = {
          height: 22,
          width: 38,
          padding: 4
        }, _sizes7)
      },
      SwitchGroup: {
        borderColor: t.colors.borderLight
      },
      Table: {
        borderColor: t.colors.borderLight,
        borderWidth: 1,
        cellBackgroundColorLight: t.colors.white,
        cellBackgroundColorDark: t.colors.gray100,
        fontType: (_fontType3 = {}, _fontType3[TABLE_SIZES.sm] = FONT_TYPES.bodySmall, _fontType3[TABLE_SIZES.md] = FONT_TYPES.bodySmall, _fontType3[TABLE_SIZES.lg] = FONT_TYPES.body, _fontType3),
        headerBackgroundColorLight: t.colors.white,
        headerBackgroundColorDark: t.colors.gray200,
        headerFontType: (_headerFontType = {}, _headerFontType[TABLE_SIZES.sm] = FONT_TYPES.bodySmallHeading, _headerFontType[TABLE_SIZES.md] = FONT_TYPES.bodySmallHeading, _headerFontType[TABLE_SIZES.lg] = FONT_TYPES.bodyHeading, _headerFontType),
        headerSortIconColor: t.colors.blue400,
        horizontalPadding: (_horizontalPadding = {}, _horizontalPadding[TABLE_SIZES.sm] = t.spacing.sm, _horizontalPadding[TABLE_SIZES.md] = t.spacing.sm, _horizontalPadding[TABLE_SIZES.lg] = t.spacing.sm, _horizontalPadding),
        rowBackgroundColorHover: t.colors.blue100,
        rowBackgroundColorSelected: t.colors.blue200,
        verticalPadding: (_verticalPadding = {}, _verticalPadding[TABLE_SIZES.sm] = 8, _verticalPadding[TABLE_SIZES.md] = 12, _verticalPadding[TABLE_SIZES.lg] = t.spacing.sm, _verticalPadding)
      },
      Tag: {
        colors: Object.assign({}, t.status, (_Object$assign = {}, _Object$assign[TAG_APPEARANCES.info] = {
          fontColor: t.colors.blue600,
          background: t.colors.blue200
        }, _Object$assign[TAG_APPEARANCES.gray] = {
          fontColor: t.colors.gray500,
          background: t.colors.coolGray200
        }, _Object$assign[TAG_APPEARANCES.closeable] = {
          fontColor: t.colors.textDark,
          background: t.colors.coolGray300
        }, _Object$assign[TAG_APPEARANCES.purple] = {
          fontColor: t.colors.purple600,
          background: t.colors.purple100
        }, _Object$assign[TAG_APPEARANCES.aqua] = {
          fontColor: t.colors.aqua600,
          background: t.colors.aqua100
        }, _Object$assign[TAG_APPEARANCES.orange] = {
          fontColor: t.colors.orange600,
          background: t.colors.orange100
        }, _Object$assign[TAG_APPEARANCES.transparent] = {
          fontColor: t.colors.white,
          background: 'rgba(0,0,0,0.6)'
        }, _Object$assign)),
        fontTypes: (_fontTypes3 = {}, _fontTypes3[TAG_SIZES.sm] = FONT_TYPES.finePrintHeading, _fontTypes3[TAG_SIZES.md] = FONT_TYPES.bodySmallHeading, _fontTypes3),
        verticalPadding: 2,
        horizontalPadding: t.spacing.xs,
        targetPadding: t.spacing.xs,
        closeButtonSize: t.icons.sizes.xs
      },
      Tabs: {
        selectedTabIndicatorColor: t.colors.brand,
        selectedTabIndicatorHeight: 4
      },
      TextButton: {
        fontColor: t.colors.link,
        fontColorInverse: t.colors.white,
        fontColorPrimary: t.colors.link,
        fontColorSecondary: t.colors.textDark,
        fontColorTertiary: t.colors.textMedium
      },
      Toast: {
        maxWidth: 500
      },
      TriggerButton: {
        helpCircleFillColor: t.colors.white,
        helpCircleFillColorActive: t.colors.gray300,
        helpCircleFillColorHover: t.colors.gray200,
        helpCircleStrokeColor: t.colors.gray400,
        helpCircleStrokeColorActive: t.colors.gray500,
        helpCircleStrokeColorHover: t.colors.gray400,
        helpQuestionFillColor: t.colors.gray600,
        helpQuestionFillColorActive: t.colors.gray600
      },
      TriggerText: {
        borderBottomColor: t.colors.brand
      },
      Upsell: {
        headingFontType: FONT_TYPES.h6,
        rowMediaSize: 48,
        columnMediaSize: 80,
        fontColorInverse: t.colors.textWhite,
        iconBackgroundColorInteractive: 'rgba(255, 255, 255, 0.3)'
      },
      UpsellBanner: {
        fontType: FONT_TYPES.h6,
        mediaSize: 44,
        boxShadow: '0 1px 1px 0 rgb(79, 79, 79, 0.3)'
      }
    };
  });
  return theme;
};

var constellation = getConstellationTheme();
export default withNamespace({
  constellation: constellation
}, 'constellation');